// One input for any fact field, driven by the field catalogue's `type`.
import type { FactField, FactValue } from '../api/types.ts';
import { Field, Input, MoneyInput, Select, Textarea } from './Form.tsx';

/**
 * Whether the dashboards can edit this field. Structured fields are editable
 * only when they're a list of strings ({ items: [...] } or a plain array);
 * the rest (officers, payroll seen, existing products) come from documents
 * and connections and are corrected in chat or by staff.
 */
export function canEdit(def: FactField, value?: FactValue): boolean {
  if (def.type !== 'object') return true;
  if (value == null) return def.field === 'trade.activities';
  const list = Array.isArray(value) ? value : (value as { items?: unknown }).items;
  return Array.isArray(list) && list.every((x) => typeof x === 'string');
}

export function toInitial(def: FactField, value: FactValue | undefined): FactValue | null {
  if (value === undefined || value === null) return def.type === 'boolean' ? null : def.type === 'object' ? { items: [] } : null;
  return value;
}

/** Valid value for the field's type, or an error message. */
export function validate(def: FactField, value: FactValue | null): string | null {
  if (value === null || value === '') return 'Please fill this in.';
  if ((def.type === 'integer' || def.type === 'money') && (!Number.isInteger(value) || (value as number) < 0)) return 'Please enter a whole number.';
  if (def.type === 'number' && (typeof value !== 'number' || Number.isNaN(value))) return 'Please enter a number.';
  if (def.type === 'object' && !(Array.isArray(value) ? value : ((value as { items?: string[] }).items ?? [])).length) return 'Please add at least one.';
  return null;
}

export function FactValueInput({ def, value, onChange, error }: { def: FactField; value: FactValue | null; onChange: (v: FactValue | null) => void; error?: string | null }) {
  const label = def.label;
  switch (def.type) {
    case 'money':
      return (
        <Field label={label} error={error}>
          <MoneyInput pence={value as number | null} onPence={onChange} />
        </Field>
      );
    case 'integer':
    case 'number':
      return (
        <Field label={label} error={error}>
          <Input
            type="number"
            inputMode={def.type === 'integer' ? 'numeric' : 'decimal'}
            min={0}
            step={def.type === 'integer' ? 1 : 'any'}
            value={value == null ? '' : String(value)}
            onChange={(e) => onChange(e.target.value === '' ? null : def.type === 'integer' ? Math.trunc(Number(e.target.value)) : Number(e.target.value))}
          />
        </Field>
      );
    case 'boolean':
      return (
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-ink">{label}</legend>
          <div className="flex gap-3">
            {[true, false].map((v) => (
              <label key={String(v)} className="flex min-h-11 flex-1 cursor-pointer items-center gap-2 rounded-md border border-line px-3 has-[:checked]:border-brand has-[:checked]:bg-brand/5">
                <input type="radio" name={def.field} checked={value === v} onChange={() => onChange(v)} className="h-4 w-4 accent-[rgb(var(--brand))]" />
                {v ? 'Yes' : 'No'}
              </label>
            ))}
          </div>
          {error && <p className="text-sm font-medium text-bad-fg">{error}</p>}
        </fieldset>
      );
    case 'enum':
      return (
        <Field label={label} error={error}>
          <Select value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || null)}>
            <option value="">Choose…</option>
            {def.options?.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      );
    case 'date':
    case 'month':
      return (
        <Field label={label} error={error}>
          <Input type={def.type} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || null)} />
        </Field>
      );
    case 'object': {
      // Only lists of strings get here (see canEdit): { items: [...] } or a plain array.
      const plain = Array.isArray(value);
      const items = (plain ? (value as string[]) : ((value as { items?: string[] }) ?? {}).items) ?? [];
      return (
        <Field label={label} hint="One per line." error={error}>
          <Textarea
            value={items.join('\n')}
            onChange={(e) => {
              const next = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean);
              onChange(plain ? next : { items: next });
            }}
          />
        </Field>
      );
    }
    default:
      return (
        <Field label={label} error={error}>
          <Input value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} />
        </Field>
      );
  }
}
